/**
 * Classify a NATS connect rejection into a retry reason + auth flag.
 *
 * The connect promise can reject for structurally different reasons:
 *
 *   - AUTH: the broker refused the credentials at the protocol handshake —
 *     an expired / empty / clock-skewed JWT surfaces as
 *     `AuthorizationError: Authorization Violation`. The fix is a RE-MINT,
 *     not a blind socket retry, so the ladder keeps its fast rungs.
 *   - MINT REJECTED: the backend answered the credential mint with a 4xx
 *     (`MINT_CLIENT_ERROR`). Retrying fast cannot help; the ladder starts
 *     at its 30 s cap and re-resolves the bearer on each rung.
 *   - MINT FAILED: the mint retry ladder exhausted on 5xx / network.
 *   - HANDSHAKE TIMEOUT: the socket opened but the broker never answered
 *     (watchdog in `connectSingleton`).
 *   - TRANSPORT: a WSS handshake error, TLS/DNS failure, or 503 from the
 *     edge. A plain reconnect (same credentials) is the right response.
 *
 * Pure + dependency-free so it can be unit-tested in isolation and reused
 * by the SDKProvider connect-failure path. Platform-agnostic.
 */

import type { ColdConnectRetryReason } from './cold-connect-retry.js';

export interface ConnectErrorClassification {
  readonly reason: ColdConnectRetryReason;
  readonly isAuth: boolean;
  readonly message: string;
}

/** Substrings (case-insensitive) that mark a credential/permission rejection. */
const AUTH_PATTERN =
  /authorization violation|authorizationerror|not authorized|authentication error|permissions violation|invalid (?:credentials|client certificate)|user authentication expired/i;

export function classifyConnectError(err: unknown): ConnectErrorClassification {
  // nats-core rejects some transport failures with an empty message; the
  // log must still say what happened, so fall back to the error's name.
  const message =
    err instanceof Error
      ? err.message !== ''
        ? err.message
        : err.name
      : typeof err === 'string'
        ? err
        : String(err);
  const obj = (err ?? null) as {
    readonly name?: unknown;
    readonly category?: unknown;
    readonly code?: unknown;
  } | null;
  if (obj !== null && typeof obj === 'object') {
    // SdkError shapes from the mint path carry a category; the message regex
    // below would misfile a 4xx as a transport error.
    if (obj.code === 'MINT_CLIENT_ERROR' || obj.category === 'auth') {
      return { reason: 'mint-rejected', isAuth: true, message };
    }
    if (
      obj.code === 'NATS_MINT_FAILED' ||
      obj.code === 'MINT_SERVER_ERROR' ||
      obj.code === 'NATS_MINT_MALFORMED'
    ) {
      return { reason: 'mint-failed', isAuth: false, message };
    }
    if (obj.name === 'HandshakeTimeoutError') {
      return { reason: 'handshake-timeout', isAuth: false, message };
    }
  }
  const isAuth = AUTH_PATTERN.test(message);
  return { reason: isAuth ? 'auth-rejected' : 'wsconnect-reject', isAuth, message };
}
